import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";

import type { LayoutAncestry } from "./LayoutAncestryContext.tsx";
import type { ElementState, ElementStateKey, ElementStateNoDefault } from "./types.ts";
import { createElementStateId, createNodeRefKey, getExistingRefKey } from "./utils.ts";

export type ActiveElementState = {
  nodeId: number;
  elementState: ElementStateNoDefault;
};

// Keeps track of which (singular) Container has an active element state other than the "default".
// Specific to FlexboxContainer atm, but could be extended to other nodes in future.
// This enables us to know which state to save styles to, when editing in the properties panel.
const activeElementStateAtom = atom<ActiveElementState | null>(null);

export const useSetActiveContainerElementState = () => {
  const setActiveContainerElementState = useSetAtom(activeElementStateAtom);
  return useCallback(
    (nodeId: number, elementState: ElementState) => {
      setActiveContainerElementState(
        elementState === "default"
          ? null
          : {
              nodeId,
              elementState,
            },
      );
    },
    [setActiveContainerElementState],
  );
};

export const useClearActiveContainerElementState = () => {
  const setActiveContainerElementState = useSetAtom(activeElementStateAtom);
  return useCallback(() => setActiveContainerElementState(null), [setActiveContainerElementState]);
};

export const useActiveElementState = (): ActiveElementState | null => {
  return useAtomValue(activeElementStateAtom);
};

export const useIsEditingElementState = (nodeId: number, ancestorIds?: readonly number[]): boolean => {
  const globalActiveState = useActiveElementState();

  return useMemo(
    () =>
      globalActiveState !== null &&
      ((nodeId === globalActiveState.nodeId || ancestorIds?.includes(globalActiveState.nodeId)) ?? false),
    [globalActiveState, nodeId, ancestorIds],
  );
};

// The active element state if it applies to the provided nodeId, potentially via its ancestors.
export const useContainingElementState = (ancestry: LayoutAncestry | null): ActiveElementState | null => {
  const globalActiveState = useActiveElementState();

  return useMemo(() => {
    return ancestry &&
      globalActiveState &&
      (ancestry.nodeId === globalActiveState.nodeId || ancestry.ancestorIds?.includes(globalActiveState.nodeId))
      ? globalActiveState
      : null;
  }, [globalActiveState, ancestry]);
};

export const useExistingNodeReferenceKey = (
  ancestry: LayoutAncestry | null,
  containingState: ActiveElementState | null,
  nodeReferences: Record<string, number> | undefined,
): string | null => {
  return useMemo(() => {
    return ancestry && containingState && nodeReferences && containingState.nodeId !== ancestry.nodeId
      ? getExistingRefKey(containingState.nodeId, nodeReferences)
      : null;
  }, [ancestry, containingState, nodeReferences]);
};

/**
 * If the containing state is for an ancestor, return either the existing node reference key,
 * or generate a new one. This should only be used in the component that will write the new
 * key to the config.
 */
export const useExistingOrNewNodeReferenceKey = (
  ancestry: LayoutAncestry | null,
  containingState: ActiveElementState | null,
  nodeReferences: Record<string, number> | undefined,
): string | null => {
  const existingNodeReferenceKey = useExistingNodeReferenceKey(ancestry, containingState, nodeReferences);
  const isAncestor = ancestry && containingState && ancestry.nodeId !== containingState.nodeId;
  return useMemo(() => {
    return isAncestor ? (existingNodeReferenceKey ?? createNodeRefKey()) : null;
  }, [isAncestor, existingNodeReferenceKey]);
};

// Used to create a key in styleByElementState using a node reference key,
// or to build the published CSS using a node id.
export const createElementStateKey = (
  state: ElementStateNoDefault,
  nodeIdOrRefKey: number | string | null,
): ElementStateKey => {
  return nodeIdOrRefKey ? `#${createElementStateId(nodeIdOrRefKey)}.${state} &` : `&.${state}`;
};

export const useDetailViewElementStateKey = (
  nodeId: number,
  ancestorIds: readonly number[] | undefined,
  nodeReferences: Record<string, number> | undefined,
): ElementStateKey | null => {
  const ancestry = useMemo(() => ({ nodeId, ancestorIds }), [nodeId, ancestorIds]);
  const containingState = useContainingElementState(ancestry);
  const nodeReferenceKey = useExistingNodeReferenceKey(ancestry, containingState, nodeReferences);
  if (containingState) {
    return createElementStateKey(containingState.elementState, nodeReferenceKey);
  }
  return null;
};
